import { larissaChain } from '@/configs/chain';

/** Shared EVM donation recipient (same address on all listed EVM chains). */
export const DONATION_EVM_ADDRESS = '0xC515A10ef43E6F2469469E9701569349A887A429' as const;

export type DonationStableMeta = {
  readonly address: `0x${string}`;
  readonly decimals: number;
};

export type DonationStablesForChain = {
  readonly usdt: DonationStableMeta;
  readonly usdc: DonationStableMeta;
};

/** Canonical USDT / USDC per chain (bridged/native where applicable). Larissa omitted — use native LRS. */
export const DONATION_STABLES_BY_CHAIN_ID: Partial<Record<number, DonationStablesForChain>> = {
  1: {
    usdt: { address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6 },
    usdc: { address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6 },
  },
  56: {
    usdt: { address: '0x55d398326f99059fF775485246999027B3197955', decimals: 18 },
    usdc: { address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', decimals: 18 },
  },
  137: {
    usdt: { address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6 },
    usdc: { address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359', decimals: 6 },
  },
  42161: {
    usdt: { address: '0xFd086bC7CD5C481DCC9C85EBE478A1C0b69FCbb9', decimals: 6 },
    usdc: { address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', decimals: 6 },
  },
  8453: {
    usdt: { address: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2', decimals: 6 },
    usdc: { address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', decimals: 6 },
  },
  10: {
    usdt: { address: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58', decimals: 6 },
    usdc: { address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85', decimals: 6 },
  },
  9898: {
    usdt: { address: '0x13c378660cddA2AfccEa093f5e0743D5Ec37a822', decimals: 6 },
    usdc: { address: '0x5B5E3Aa46Cf811eB545617dCc25856e06D07D7ad', decimals: 6 },
  },
};

export function getDonationStables(chainId: number): DonationStablesForChain | null {
  return DONATION_STABLES_BY_CHAIN_ID[chainId] ?? null;
}

export const DONATION_SOL_ADDRESS = '3iSqbefHpRrgV2t4RzkzBD7ys9T61QMkoqoAqEJ8JTXd';

export const DONATION_BTC_ADDRESS = 'bc1qtqlfcw39my2hsglqw0a52udth2qy0n4cpzrxx3';

export type DonationEvmChainMeta = {
  id: number;
  name: string;
  nativeSymbol: string;
  rpcUrl: string;
  explorerAddressUrl: (address: string) => string;
};

export const DONATION_EVM_CHAINS: DonationEvmChainMeta[] = [
  {
    id: 1,
    name: 'Ethereum',
    nativeSymbol: 'ETH',
    rpcUrl: 'https://eth.llamarpc.com',
    explorerAddressUrl: (a) => `https://etherscan.io/address/${a}`,
  },
  {
    id: larissaChain.id,
    name: larissaChain.name,
    nativeSymbol: larissaChain.nativeCurrency.symbol,
    rpcUrl: larissaChain.rpcUrls.default.http[0],
    explorerAddressUrl: (a) => `${larissaChain.blockExplorers.default.url}/address/${a}`,
  },
  {
    id: 56,
    name: 'BNB Smart Chain',
    nativeSymbol: 'BNB',
    rpcUrl: 'https://bsc-dataseed.bnbchain.org',
    explorerAddressUrl: (a) => `https://bscscan.com/address/${a}`,
  },
  {
    id: 137,
    name: 'Polygon',
    nativeSymbol: 'POL',
    rpcUrl: 'https://1rpc.io/matic',
    explorerAddressUrl: (a) => `https://polygonscan.com/address/${a}`,
  },
  {
    id: 42161,
    name: 'Arbitrum One',
    nativeSymbol: 'ETH',
    rpcUrl: 'https://arb1.arbitrum.io/rpc',
    explorerAddressUrl: (a) => `https://arbiscan.io/address/${a}`,
  },
  {
    id: 8453,
    name: 'Base',
    nativeSymbol: 'ETH',
    rpcUrl: 'https://mainnet.base.org',
    explorerAddressUrl: (a) => `https://basescan.org/address/${a}`,
  },
  {
    id: 10,
    name: 'Optimism',
    nativeSymbol: 'ETH',
    rpcUrl: 'https://mainnet.optimism.io',
    explorerAddressUrl: (a) => `https://optimistic.etherscan.io/address/${a}`,
  },
];

/** EIP-681 style URI for wallet QR deep links (per-chain). */
export function donationEvmQrUri(chainId: number, address: string): string {
  return `ethereum:${address}@${chainId}`;
}

export const SOLANA_RPC = 'https://api.mainnet-beta.solana.com';

export function donationSolanaQrUri(address: string): string {
  return `solana:${address}`;
}

export function donationBitcoinQrUri(address: string): string {
  return `bitcoin:${address}`;
}

/** Block explorer URL for a native transfer on supported EVM chains. */
export function donationEvmTxExplorerUrl(chainId: number, txHash: string): string | null {
  const base: Record<number, string> = {
    1: 'https://etherscan.io',
    56: 'https://bscscan.com',
    137: 'https://polygonscan.com',
    42161: 'https://arbiscan.io',
    8453: 'https://basescan.org',
    10: 'https://optimistic.etherscan.io',
    [larissaChain.id]: larissaChain.blockExplorers.default.url,
  };
  const b = base[chainId];
  return b ? `${b}/tx/${txHash}` : null;
}
